export interface ActivityRow {
  id: string;
  actor: string;
  occurredAtUtc: string;
  action: string;
  description: string;
}

const SKIP = new Set(['id', 'entityId', 'entityType']);

function verbFor(action: string): string {
  const normalized = (action ?? '').trim().toLowerCase();
  if (normalized === 'create' || normalized === 'created') {
    return 'Created';
  }
  if (normalized === 'update' || normalized === 'updated') {
    return 'Updated';
  }
  if (normalized === 'delete' || normalized === 'deleted') {
    return 'Deleted';
  }
  if (!normalized) {
    return 'Changed';
  }
  return normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function describeActivityChange(
  action: string,
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined
): string {
  const verb = verbFor(action);

  const diffs = diffFields(before, after);
  if (diffs.length > 0) {
    return diffs.join('; ');
  }

  const name = nameFrom(after) ?? nameFrom(before);
  return name ? `${verb} ${name}` : verb;
}

function nameFrom(row: Record<string, unknown> | null | undefined): string | undefined {
  if (!row) {
    return undefined;
  }
  for (const key of ['name', 'legalName', 'code', 'title', 'number', 'periodId']) {
    const value = pick(row, key);
    if (typeof value === 'string' && value.trim()) {
      return value;
    }
  }
  return undefined;
}

function diffFields(
  before: Record<string, unknown> | null | undefined,
  after: Record<string, unknown> | null | undefined
): string[] {
  if (!before || !after) {
    return [];
  }
  const rows: string[] = [];
  for (const [key, next] of Object.entries(after)) {
    if (SKIP.has(key)) {
      continue;
    }
    const prev = pick(before, key);
    const left = scalar(prev);
    const right = scalar(next);
    if (left !== right) {
      rows.push(`${key} ${left} → ${right}`);
    }
  }
  return rows;
}

function scalar(value: unknown): string {
  if (value == null) {
    return '—';
  }
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }
  return JSON.stringify(value);
}

function pick(row: Record<string, unknown>, key: string): unknown {
  const match = Object.keys(row).find((candidate) => candidate.toLowerCase() === key.toLowerCase());
  return match === undefined ? undefined : row[match];
}

export function activityFromRequestLog(
  entries: { atUtc: string; actorDisplayName?: string; actorUsername?: string; message: string }[]
): ActivityRow[] {
  return entries.map((entry, index) => ({
    id: `request-activity-${index}-${entry.atUtc}`,
    actor: entry.actorDisplayName?.trim() || entry.actorUsername?.trim() || '',
    occurredAtUtc: entry.atUtc ?? '',
    action: actionFromRequestMessage(entry.message),
    description: entry.message ?? ''
  }));
}

function actionFromRequestMessage(message: string): string {
  const text = (message ?? '').toLowerCase();
  if (text.includes('created')) {
    return 'Created';
  }
  if (text.includes('status changed') || text.includes('updated')) {
    return 'Updated';
  }
  if (text.includes('deleted') || text.includes('rejected')) {
    return 'Deleted';
  }
  return 'Changed';
}

export function activityFromMovementLog(
  entries: { atUtc?: string; summary?: string; materialTransferNumber?: string | null }[]
): ActivityRow[] {
  return entries.map((entry, index) => {
    const description = entry.summary?.trim() || 'Inventory movement';
    return {
      id: `movement-${index}-${entry.atUtc ?? index}`,
      actor: '',
      occurredAtUtc: entry.atUtc ?? '',
      action: actionFromRequestMessage(description),
      description: entry.materialTransferNumber
        ? `${description} (${entry.materialTransferNumber})`
        : description
    };
  });
}

export function activityFromNotes(notes: string[], actor = ''): ActivityRow[] {
  return notes.map((note, index) => ({
    id: `note-${index}`,
    actor,
    occurredAtUtc: '',
    action: note,
    description: describeActivityChange(note, null, null)
  }));
}

export function activityFromAudit(row: {
  id: string;
  actor: string;
  occurredAtUtc: string;
  action: string;
  description?: string;
  beforeJson?: string | null;
  afterJson?: string | null;
}): ActivityRow {
  if (row.description?.trim()) {
    return {
      id: row.id,
      actor: row.actor ?? '',
      occurredAtUtc: row.occurredAtUtc ?? '',
      action: row.action,
      description: row.description
    };
  }
  return {
    id: row.id,
    actor: row.actor ?? '',
    occurredAtUtc: row.occurredAtUtc ?? '',
    action: row.action ?? '',
    description: describeActivityChange(row.action ?? '', parseJson(row.beforeJson), parseJson(row.afterJson))
  };
}

function parseJson(raw?: string | null): Record<string, unknown> | null {
  if (!raw?.trim()) {
    return null;
  }
  try {
    const value = JSON.parse(raw) as unknown;
    return value && typeof value === 'object' && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}
